import { ReactElement, useContext, useEffect, useState } from "react";
import { IconButton, Paper, Tooltip } from "@mui/material";
import { AddToQueue, Dashboard, FolderOpen, RemoveFromQueue, Save } from "@mui/icons-material";
import MainDialog from "./mainDialog";
import { useAsyncFetch } from "../../../system/useFetch";
import { IResponseData, ISelectedNode, Modules } from "../../../types";
import { ComponentType, IDashboard, IDashboardComponent, IGripPosition } from "../types";
import { useDispatch } from "react-redux";
import { SystemContext } from "../../../context/systemContext";
import OpenDashboard from "./openDashboard";
import { addAlert } from "../../../actions";
import AddComponent from "./addComponent";
import { getEmptyDashboard } from "../utils";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import AddchartIcon from "@mui/icons-material/Addchart";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { cloneObject } from "../../../system/utils";

interface IProps {
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  runtime: boolean;
  setRuntime: (runtime: boolean) => void;
  setPanelPosition: (panelPosition: IGripPosition | null) => void;
  selectedNode: ISelectedNode;
}

const Toolbar = ({ dashboard, setDashboard, runtime, setRuntime, setPanelPosition, selectedNode }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext = useContext(SystemContext);
  const [open, setOpen] = useState(false);
  const [openAddComponent, setOpenAddComponent] = useState({ open: false, customData: {
    column: 1,
    row: 1,
    columnSpan: 1,
    rowSpan: 1,
    componentType: ComponentType.Indicators
  } });
  const [openDialog, setOpenDialog] = useState(false);
  const [executeRequest] = useAsyncFetch<IResponseData<number>>();
  const [layout, setLayout] = useState<number[][]>([]);

  useEffect(() => {
    const map = [
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    ];

    dashboard.layout.components.forEach(component => {
      for (let row = component.row - 1; row < component.row + component.rowSpan - 1; row++) {
        for (let column = component.column - 1; column < component.column + component.columnSpan - 1; column++) {
          map[row][column] += 1;
        }
      }
    });

    setLayout(map);
  }, [dashboard]);

  const onSave = () => {
    executeRequest({
      module: "dashboard",
      query: "saveDashboard",
      payload: {
        dashboard: {
          ...dashboard,
          moduleID: Modules.DASHBOARD_RT,
          nodeType: selectedNode.type,
          nodeID: selectedNode.nodeID,
          userID: systemContext.user?.userID ?? 0
        }
      }
    }).then(response => {
      if (response?.status && response.data) {
        if (!dashboard.dashboardID) {
          setDashboard({ ...dashboard, dashboardID: response.data });
        }

        dispatch(addAlert({ message: "The layout was saved!", severity: "success", hash: undefined }));
      }
    });
  };

  const onCloneDashboard = () => {
    systemContext.showMessage({
      title: "Clone Layout",
      color: "info",
      message: "Do you want to clone the Layout?",
      callback: () => {
        const cloned = cloneObject(dashboard);
        cloned.dashboardID = null;
        cloned.name = `${cloned.name} - cloned`;

        setDashboard(cloned);
      }
    });
  };


  const onNewDashboard = () => {
    setDashboard(getEmptyDashboard());
  };

  const editAllowed = dashboard && (dashboard.userID === 0 || systemContext.user?.userID === dashboard.userID || dashboard.isEditable);

  const onRemoveDashboard = () => {
    systemContext.showMessage({
      title: "Delete Layout",
      color: "danger",
      message: "Do you want to delete the Layout?",
      callback: () => {
        executeRequest({
          module: "dashboard",
          query: "deleteDashboard",
          payload: { dashboardID: dashboard?.dashboardID }
        }).then(response => {
          if (response?.status) {
            dispatch(addAlert({ message: "The layout was deleted!", severity: "error", hash: undefined }));
            onNewDashboard();
          }
        });
      }
    });
  };

  const onAddComponent = () => {
    const maxIndex = 12;

    for (let row = 0; row < maxIndex; row++) {
      for (let col = 0; col < maxIndex; col++) {
        if(layout[row][col] === 0) {
          const length = dashboard.layout.components.length;
          const component = length > 0 ? dashboard.layout.components[length - 1] : null;
          const newColumn = col + 1;
          const newRow = row + 1;
          const columnSpan = component ? component.columnSpan : 2;
          const rowSpan = component ? component.rowSpan : 2;

          setOpenAddComponent({
            open: true,
            customData: {
              column: newColumn,
              row: newRow,
              columnSpan: maxIndex >= newColumn + columnSpan - 1 ? columnSpan : maxIndex - newColumn + 1,
              rowSpan: maxIndex >= newRow + rowSpan - 1 ? rowSpan : maxIndex - newRow + 1,
              componentType: component ? component.componentType : ComponentType.Indicators
            } });

          return;
        }
      }
    }
  };

  const onAddComponentAccept = (component: IDashboardComponent<any>) => {
    const newDashboard = { ...dashboard };
    newDashboard.layout.components.push(component);
    setDashboard(newDashboard);
    setOpenAddComponent({ ...openAddComponent, open: false });
    setPanelPosition(null);

  };

  const onAddComponentClose = () => {
    setOpenAddComponent({ ...openAddComponent, open: false });
    setPanelPosition(null);
  };

  return (
    <Paper className="p-1 display-flex-row align-items-center" sx={{ gridColumn: "1/span 1" }}>
      <IconButton color="primary" onClick={() => setOpenDialog(true)} disabled={runtime}>
        <Tooltip title={"Open"}>
          <FolderOpen/>
        </Tooltip>
      </IconButton>
      <IconButton color="success" onClick={onSave} disabled={runtime}>
        <Tooltip title={"Save"}>
          <Save/>
        </Tooltip>
      </IconButton>
      <IconButton color="primary" onClick={onCloneDashboard}  disabled={runtime}>
        <Tooltip title={"Clone Layout"}>
          <FileCopyIcon/>
        </Tooltip>
      </IconButton>
      <IconButton color="primary" onClick={onNewDashboard} disabled={runtime}>
        <Tooltip title={"New Layout"}>
          <AddToQueue/>
        </Tooltip>
      </IconButton>
      <IconButton color="error" disabled={!dashboard?.dashboardID || runtime} onClick={onRemoveDashboard} >
        <Tooltip title={"Delete Layout"}>
          <RemoveFromQueue/>
        </Tooltip>
      </IconButton>
      <IconButton color="primary" onClick={() => setOpen(true)} disabled={!editAllowed || runtime}>
        <Tooltip title={"Layout"}>
          <Dashboard/>
        </Tooltip>
      </IconButton>

      <IconButton className="mr-2 ml-2" color="primary" onClick={onAddComponent} disabled={runtime}>
        <Tooltip title={"Add Component"}>
          <AddchartIcon/>
        </Tooltip>
      </IconButton>

      {runtime
        ? <IconButton color="warning" onClick={() => setRuntime(false)}>
          <Tooltip title={"Stop"}>
            <StopIcon/>
          </Tooltip>
        </IconButton>
        : <IconButton color="success" onClick={() => setRuntime(true)}>
          <Tooltip title={"Start"}>
            <PlayArrowIcon/>
          </Tooltip>
        </IconButton>
      }

      <div className="flex-grow-1"/>
      <strong className="mr-2" style={{ fontSize: "1.2em" }}>{dashboard?.name}</strong>

      {dashboard && open &&
        <MainDialog dashboard={dashboard} setDashboard={setDashboard} open={open} setOpen={setOpen}/>
      }
      <OpenDashboard selectedNode={selectedNode} open={openDialog} onClose={() => setOpenDialog(false)}
        onOpenDashboard={setDashboard}/>
      {openAddComponent.open &&
        <AddComponent onClose={onAddComponentClose} onAccept={onAddComponentAccept} setPanelPosition={setPanelPosition}
          customData={openAddComponent.customData}/>
      }
    </Paper>
  );
};

export default Toolbar;
